/** @format */

import {useState} from 'react';
import type {ReactNode} from 'react';

import {add, differenceInDays} from 'date-fns';

import {SelectChangeEvent} from '@mui/material/Select';

import {YalperAnalyticAttributionModelEnum} from 'graphql/generated/types';

import {FormState, KpiType} from '../../../types';
import {getInitialFormState, validateBudgetInput} from '../helpers';
import {MAX_CALENDAR_RANGE_DAYS, MIN_CALENDAR_RANGE_DAYS} from '../constants';

const validateDateRange = (startDate: Date, endDate: Date) => {
  const daysDiff = differenceInDays(endDate, startDate);
  const minDays = MIN_CALENDAR_RANGE_DAYS;
  const maxDays = MAX_CALENDAR_RANGE_DAYS;

  return {
    isValid: daysDiff >= minDays && daysDiff <= maxDays,
    daysDiff,
    tooShort: daysDiff < minDays,
    tooLong: daysDiff > maxDays,
  };
};

export const useFormState = () => {
  const [state, setState] = useState<FormState>(getInitialFormState());

  const handleCampaignChange = (campaignIds: string[]) => {
    setState(prevState => ({
      ...prevState,
      campaigns: campaignIds,
    }));
  };

  const handleGoalChange = (goalIds: string[]) => {
    setState(prevState => ({
      ...prevState,
      goals: goalIds,
    }));
  };

  const handleKpiChange = (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
    setState(prevState => ({...prevState, kpi: event.target.value as KpiType}));
  };

  const handleKpiAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (validateBudgetInput(value)) {
      setState(prevState => ({...prevState, kpiAmount: value}));
    }
  };

  const handleAttributionChange = (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
    setState(prevState => ({
      ...prevState,
      attributionModel: event.target.value as YalperAnalyticAttributionModelEnum,
    }));
  };

  const handleBudgetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (validateBudgetInput(value)) {
      setState(prevState => ({...prevState, budget: value}));
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setState(prevState => {
        const yesterday = add(new Date(), {days: -1});
        const minStartDate = add(date, {days: MIN_CALENDAR_RANGE_DAYS});

        if (minStartDate > yesterday) {
          return {
            ...prevState,
            startDate: add(yesterday, {days: -MIN_CALENDAR_RANGE_DAYS}),
            endDate: yesterday,
          };
        }

        const newState = {...prevState, startDate: date};

        if (prevState.endDate) {
          const validation = validateDateRange(date, prevState.endDate);

          if (!validation.isValid) {
            let newEndDate = prevState.endDate;
            if (validation.tooShort) {
              newEndDate = add(date, {days: 30});
            } else if (validation.tooLong) {
              newEndDate = add(date, {days: 180});
            }
            newState.endDate = newEndDate > yesterday ? yesterday : newEndDate;
          }
        }

        return newState;
      });
    } else {
      setState(prevState => ({...prevState, startDate: null}));
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setState(prevState => {
        const newState = {...prevState, endDate: date};

        if (prevState.startDate) {
          const validation = validateDateRange(prevState.startDate, date);

          if (!validation.isValid) {
            if (validation.tooShort) {
              newState.startDate = add(date, {days: -30});
            } else if (validation.tooLong) {
              newState.startDate = add(date, {days: -180});
            }
          }
        }

        return newState;
      });
    } else {
      setState(prevState => ({...prevState, endDate: null}));
    }
  };

  const resetForm = () => {
    setState(getInitialFormState());
  };

  return {
    state,
    resetForm,
    handlers: {
      handleCampaignChange,
      handleGoalChange,
      handleKpiChange,
      handleKpiAmountChange,
      handleAttributionChange,
      handleBudgetChange,
      handleStartDateChange,
      handleEndDateChange,
    },
  };
};
